import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster, toast } from "react-hot-toast";
import { loginProvider, signupLandlord } from "../api/enrollment-api";
import { useAuth } from "../context/authContext";

const defaultForm = {
  businessName: "",
  email: "",
  phoneNumber: "",
  password: "",
  confirmPassword: "",
};

export const generateNetworkCode = () => {
  const letters = "abcdefghijklmnopqrstuvwxyz";
  return Array.from(
    { length: 3 },
    () => letters[Math.floor(Math.random() * letters.length)],
  ).join("");
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

const isAssignedNetwork = (network) =>
  Boolean(network?.trim()) && network.trim().toLowerCase() !== "unassigned";

export default function Enroll() {
  const navigate = useNavigate();
  const { applyAuthSession } = useAuth();
  const [formData, setFormData] = useState(defaultForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  const images = useMemo(
    () => [
      require("../assets/images/girl-laptop.jpg"),
      require("../assets/images/men-happy.jpg"),
      require("../assets/images/girl-microwave.jpg"),
    ],
    [],
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [images.length]);

  const updateField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const setFieldError = (name, message) => {
    setFieldErrors((prev) => ({ ...prev, [name]: message }));
  };

  const validateForm = () => {
    const requiredFields = [
      ["businessName", "House name"],
      ["email", "Email"],
      ["phoneNumber", "Phone number"],
      ["password", "Password"],
      ["confirmPassword", "Confirm password"],
    ];

    const missingField = requiredFields.find(
      ([key]) => !String(formData[key] || "").trim(),
    );

    if (missingField) {
      setFieldError(missingField[0], `${missingField[1]} is required`);
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      setFieldError("email", "Please enter a valid email address");
      return false;
    }

    if (formData.password.length < 8) {
      setFieldError("password", "Password must be at least 8 characters");
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setFieldError("confirmPassword", "Passwords do not match");
      return false;
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    const email = formData.email.trim().toLowerCase();
    const businessName = formData.businessName.trim();
    const network = generateNetworkCode();

    setLoading(true);
    try {
      const payload = {
        name: businessName,
        email,
        phone_number: formData.phoneNumber.trim(),
        password: formData.password,
        role_name: "landlord",
        network,
        portal_url: window.location.origin,
      };

      const signupResponse = await signupLandlord(payload);

      const authSession = signupResponse?.token
        ? signupResponse
        : await loginProvider({ email, password: formData.password });
      const sessionNetwork = authSession.user?.network?.trim();
      const enrollmentNetwork = isAssignedNetwork(sessionNetwork)
        ? sessionNetwork
        : network;

      applyAuthSession({
        ...authSession,
        user: {
          ...authSession.user,
          network: enrollmentNetwork,
        },
      });
      sessionStorage.setItem(
        "landlord_profile_setup",
        JSON.stringify({
          businessName,
          email,
          phoneNumber: formData.phoneNumber.trim(),
          network: enrollmentNetwork,
        }),
      );

      navigate("/dashboard", { replace: true });
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to create provider account."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-black px-4 py-6 font-nexa">
      <AuthBackdrop images={images} currentImage={currentImage} />
      <div className="relative z-20 mb-5">
        <img
          src={require("../assets/images/logo-white.png")}
          alt="NanoWattz Logo"
          width={250}
        />
      </div>

      <div className="relative z-20 w-full max-w-xl">
        <form
          onSubmit={handleSubmit}
          className="w-full rounded-xl bg-black/55 p-6 shadow-2xl md:p-8"
        >
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-white md:text-3xl">
              Enroll as Landlord
            </h1>
            <p className="mt-2 text-sm text-primary-light">
              Create your login with your house details.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Field
              label="House name"
              name="businessName"
              value={formData.businessName}
              onChange={updateField}
              error={fieldErrors.businessName}
              required
            />
            <Field
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={updateField}
              error={fieldErrors.email}
              required
            />
            <Field
              label="Phone number"
              name="phoneNumber"
              type="tel"
              value={formData.phoneNumber}
              onChange={updateField}
              error={fieldErrors.phoneNumber}
              required
            />
            <div />
            <Field
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={updateField}
              error={fieldErrors.password}
              required
            />
            <Field
              label="Confirm password"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={updateField}
              error={fieldErrors.confirmPassword}
              required
            />
          </div>

          <div className="mt-6 flex flex-col gap-3 border-t border-white/15 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <Link
              className="text-sm font-bold text-white hover:text-primary-normal"
              to="/"
            >
              Back to login
            </Link>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center justify-center gap-2 rounded bg-primary-normal px-5 py-3 font-bold text-primary-dark transition hover:bg-primaryDull disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              Create
            </button>
          </div>
        </form>
      </div>

      <AuthFooter />
      <Toaster position="top-center" />
    </div>
  );
}

export const Field = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  inputMode,
  error,
}) => (
  <label className="flex flex-col gap-1 text-sm text-white">
    <span className="font-bold">
      {label}
      {required && <span className="ml-1 text-primary-error">*</span>}
    </span>
    <input
      type={type}
      name={name}
      value={value}
      inputMode={inputMode}
      onChange={(event) => onChange(name, event.target.value)}
      className="h-11 rounded border border-white/25 bg-transparent px-3 text-white outline-none transition placeholder:text-white/45 focus:border-primaryDull focus:ring-2 focus:ring-primary-normal/25"
      required={required}
    />
    {error && (
      <span className="text-xs font-bold text-primary-error">{error}</span>
    )}
  </label>
);

export const AuthFooter = () => (
  <div className="absolute bottom-5 z-20">
    <p className="font-nexa w-full text-center text-white">
      All rights reserved{" "}
      <a
        className="text-white hover:text-primaryDull decoration-transparent"
        href="/about"
        target="_blank"
        rel="noreferrer"
      >
        THE GRID 1.0
      </a>
    </p>
  </div>
);

export const AuthBackdrop = ({ images, currentImage }) => (
  <>
    <div className="absolute inset-0 w-full h-full overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50 z-10"></div>
      {images.map((img, index) => (
        <img
          key={index}
          src={img}
          alt={`slide-${index}`}
          className={`absolute h-full w-full object-cover transition-opacity duration-1000 ${index === currentImage ? "opacity-100" : "opacity-0"}`}
        />
      ))}
    </div>
    <div className="absolute top-4 left-0 w-full flex gap-2 px-4 z-20">
      {images.map((_, index) => (
        <div
          key={index}
          className="h-1 flex-1 rounded-full bg-white relative overflow-hidden"
        >
          {index <= currentImage && (
            <div
              className="absolute left-0 top-0 h-full bg-primary-normal transition-all duration-[5000ms]"
              style={{
                width: index === currentImage ? "100%" : "100%",
                animation:
                  index === currentImage
                    ? "fillProgress 5s linear forwards"
                    : "none",
              }}
            />
          )}
        </div>
      ))}
    </div>
    <style>
      {`
        @keyframes fillProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}
    </style>
  </>
);
